import { Droppable } from '@hello-pangea/dnd';
import { Snowflake } from 'lucide-react';
import KanbanCard from './KanbanCard';
import type { Operacao, Esteira } from '../types';
import { ESTEIRAS_CONFIG } from '../types';

interface SubStatusColumnProps {
  esteira: Esteira;
  subStatusNum: number;   // 1-based, posição combinada (bucket "Crédito" concatena ENTRADA+CREDITO)
  total: number;          // SPEC_LAYOUT_UX_V1 (E1) - total de colunas do bucket, do backend (etapas.length)
  label: string;
  operacoes: Operacao[];
  onCardClick: (op: Operacao) => void;
}

// Paleta base por esteira: [bg-claro, bg-médio, bg-forte, texto, borda]
const PALETA: Record<number, { light: string; mid: string; full: string; text: string; border: string; dot: string }> = {
  0: { light: 'bg-sky-50',    mid: 'bg-sky-200',    full: 'bg-sky-600',    text: 'text-sky-700',    border: 'border-sky-200',  dot: 'bg-sky-500'    },
  1: { light: 'bg-amber-50',  mid: 'bg-amber-200',  full: 'bg-amber-500',  text: 'text-amber-700',  border: 'border-amber-200',dot: 'bg-amber-400'  },
  2: { light: 'bg-purple-50', mid: 'bg-purple-200', full: 'bg-purple-600', text: 'text-purple-700', border: 'border-purple-200',dot: 'bg-purple-500' },
  3: { light: 'bg-emerald-50',mid: 'bg-emerald-200',full: 'bg-emerald-600',text: 'text-emerald-700',border: 'border-emerald-200',dot:'bg-emerald-500'},
};

function headerStyle(esteira: Esteira, subStatusNum: number, total: number): string {
  const p = PALETA[esteira];
  const ratio = (subStatusNum - 1) / Math.max(total - 1, 1); // 0 → 1

  if (ratio < 0.33) return `${p.light} ${p.text} border-b ${p.border}`;
  if (ratio < 0.67) return `${p.mid} ${p.text} border-b ${p.border}`;
  return `${p.full} text-white`;
}

function dotStyle(esteira: Esteira, subStatusNum: number, total: number): string {
  const p = PALETA[esteira];
  const ratio = (subStatusNum - 1) / Math.max(total - 1, 1);
  if (ratio < 0.33) return `${p.dot} opacity-50`;
  if (ratio < 0.67) return `${p.dot} opacity-75`;
  return `${p.dot}`;
}

function counterStyle(subStatusNum: number, total: number): string {
  const ratio = (subStatusNum - 1) / Math.max(total - 1, 1);
  if (ratio >= 0.67) return 'bg-white/20 text-white';
  return 'bg-white/60 text-slate-700';
}

export default function SubStatusColumn({
  esteira,
  subStatusNum,
  total,
  label,
  operacoes,
  onCardClick,
}: SubStatusColumnProps) {
  const config = ESTEIRAS_CONFIG[esteira];
  const droppableId = `sub-${subStatusNum}`;

  const ativas     = operacoes.filter((op) => op.status !== 'CONGELADA');
  const congeladas = operacoes.filter((op) => op.status === 'CONGELADA');

  const hStyle  = headerStyle(esteira, subStatusNum, total);
  const dStyle  = dotStyle(esteira, subStatusNum, total);
  const cStyle  = counterStyle(subStatusNum, total);

  return (
    <div className="flex flex-col bg-white rounded-2xl border border-slate-200 w-60 shrink-0 h-full shadow-sm">
      {/* Header com gradiente de intensidade por posição da fase */}
      <div className={`rounded-t-2xl px-3 py-2.5 ${hStyle}`}>
        <div className="flex items-center justify-between gap-1.5">
          {/* Indicador de posição + label */}
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex items-center gap-0.5 shrink-0">
              {Array.from({ length: total }).map((_, i) => (
                <div
                  key={i}
                  className={`rounded-full transition-all ${
                    i < subStatusNum
                      ? `${dStyle} ${i === subStatusNum - 1 ? 'w-2.5 h-2.5' : 'w-1.5 h-1.5 opacity-40'}`
                      : 'w-1.5 h-1.5 bg-current opacity-15'
                  }`}
                />
              ))}
            </div>
            <h3 className="text-xs font-semibold leading-snug truncate" title={label}>
              {label}
            </h3>
          </div>

          {/* Contador */}
          <span className={`shrink-0 text-xs font-bold px-1.5 py-0.5 rounded-full ${cStyle}`}>
            {operacoes.length}
          </span>
        </div>

        {/* Sub-linha: responsável + posição numérica */}
        <div className="flex items-center justify-between mt-0.5 opacity-60">
          <p className="text-[10px]">{config.responsavel}</p>
          <p className="text-[10px] font-mono">{subStatusNum}/{total}</p>
        </div>
      </div>

      {/* Cards droppable */}
      <Droppable droppableId={droppableId}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`
              flex-1 overflow-y-auto p-2 space-y-2 min-h-[100px] rounded-b-2xl
              transition-colors duration-150
              ${snapshot.isDraggingOver ? 'bg-slate-50' : ''}
            `}
          >
            {ativas.map((op, i) => (
              <KanbanCard key={op.id} operacao={op} index={i} onClick={onCardClick} />
            ))}

            {congeladas.length > 0 && (
              <>
                {ativas.length > 0 && (
                  <div className="flex items-center gap-1 my-1">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-slate-400"><Snowflake size={10} /></span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                )}
                {congeladas.map((op, i) => (
                  <KanbanCard key={op.id} operacao={op} index={ativas.length + i} onClick={onCardClick} />
                ))}
              </>
            )}

            {operacoes.length === 0 && (
              <div className="flex-1 flex items-center justify-center py-6">
                <p className="text-[11px] text-slate-400 text-center">-</p>
              </div>
            )}

            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
}
